import React, { type ElementType, useEffect, useState } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateView } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';

interface CustomDatePickerProps {
  label: string;
  format: 'YYYY-MM-DD' | 'DD-MM-YYYY' | 'YYYY-MM' | 'MM-DD' | 'YYYY' | 'MM/YYYY';
  value?: string;
  onChange: (val: string) => void;
  fullWidth?: boolean;
  required?: boolean;
  disabled?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  error?: boolean;
  onBlur?: () => void;
  helperText?: string;
  views?: readonly DateView[];
  calendarIcon?: ElementType;
  styles?: any;
  slotProps?: any;
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  label,
  format = 'YYYY-MM-DD',
  value,
  onChange,
  fullWidth,
  required,
  disabled,
  minDate,
  maxDate,
  error,
  onBlur,
  helperText,
  views,
  calendarIcon,
  styles,
  slotProps,
}) => {
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);

  // Sync internal state with external value prop
  useEffect(() => {
    if (value) {
      try {
        const parsedDate = dayjs(value, format);

        // Only update if the date has actually changed
        if (selectedDate && parsedDate.isValid() && selectedDate.isSame(parsedDate, 'day')) {
          return;
        }

        setSelectedDate(parsedDate.isValid() ? parsedDate : null);
      } catch (error) {
        console.error('Error parsing date:', error);
        setSelectedDate(null);
      }
    } else {
      setSelectedDate(null);
    }
  }, [value, format]);

  const dateFormatPicker = (format: string) => {
    let views: readonly DateView[];
    switch (format) {
      case 'YYYY-MM-DD':
      case 'DD-MM-YYYY':
        views = ['year', 'month', 'day'];
        break;
      case 'YYYY-MM':
      case 'MM/YYYY':
        views = ['year', 'month'];
        break;
      case 'MM-DD':
        views = ['month', 'day'];
        break;
      case 'YYYY':
        views = ['year'];
        break;
      default:
        views = ['year', 'month'];
        break;
    }
    return views;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        {...(calendarIcon ? { slots: { openPickerIcon: calendarIcon } } : {})}
        views={views ?? dateFormatPicker(format)}
        format={format}
        label={`${label}${required ? ' *' : ''}`}
        value={selectedDate}
        onChange={(newValue) => {
          onChange(newValue?.format(format) ?? '');
        }}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        sx={{
          width: fullWidth ? '100%' : 'auto',
          '& .MuiInputBase-root': {
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: '1.875rem',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#737F86',
          },
          // Override disabled styles to maintain same appearance
          '& .MuiInputBase-root.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.87) !important',
            backgroundColor: 'transparent !important',
            opacity: '1 !important',
          },
          '& .MuiInputBase-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.38) !important',
          },
          '& .MuiInputLabel-root.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.6) !important',
          },
          '& .MuiInputBase-input.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.87) !important',
            WebkitTextFillColor: 'rgba(0, 0, 0, 0.38) !important',
          },
          ...styles,
        }}
        slotProps={{
          textField: {
            inputProps: {
              readOnly: true,
            },
            error: !!error,
            helperText,
            onBlur,
          },
          popper: {
            sx: {
              zIndex: 99999,
            },
          },
          ...slotProps,
        }}
      />
    </LocalizationProvider>
  );
};

export default CustomDatePicker;
